import type { LoginPayload } from './types/LoginPayload';
import type { SignupPayload } from './types/SignupPayload';
import {
  getAuth,
  createUserWithEmailAndPassword,
  initializeAuth,
  indexedDBLocalPersistence,
  signInWithEmailAndPassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  signOut,
  signInWithCredential,
  updatePassword,
  GoogleAuthProvider,
  OAuthProvider,
  deleteUser,
  getAdditionalUserInfo,
  type UserCredential,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { firebaseApp, firestore } from '@/services/firebase';
import { httpService } from '../common/http.service';
import type { ApiResponse } from '../common/types/api-response';
import type { User } from '../user/types/user';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import type { ChangePasswordPayload } from '@/api/auth/types/ChangePasswordPayload';
import { disconnectSocket } from '../../services/socket.service';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import type { AppVersion } from './types/AppVersion';
import {
  NO_APP_VERSION_UPDATES,
  resolveAppVersionStatus,
  type AppVersionCandidates,
  type AppVersionStatus,
} from './app-version-status';
import environment from '@/environment';
import logger from '@/services/logger';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { clearPendingRegistration, getPendingRegistration, setPendingRegistration } from './pending-registration';
import { isUserCancellationError } from './utils/firebase-error-handler';
import { logEvent } from '@/services/analytics';
import { setMetaAccount, trackMetaRegistrationCompleted } from '@/services/meta-app-events';
import { clearPrivacyConsents } from '@/services/privacy-consents';
import { setSessionReplayAccount } from '@/services/session-replay';
import { buildDisplayName, sanitizeNamePart } from '@/utils/display-name';
import { useAuthState } from '@/state/user';
import { buildPasswordResetContinueUrl, isAccountNotFoundError, isContinueUrlError } from './password-reset';

/** The server adds metaEventId only when this call created the account. */
type RegisterResponse = ApiResponse<User> & { metaEventId?: string };

const { setBackendUserProvisioned } = useAuthState();

const whichAuth = () => {
  let auth;
  if (Capacitor.isNativePlatform()) {
    auth = initializeAuth(firebaseApp, {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    auth = getAuth(firebaseApp);
  }
  return auth;
};

export const firebaseAuth = whichAuth();

export async function logout() {
  // Stop Session Replay before anything else: the recording belongs to this account.
  setSessionReplayAccount(null);
  await logEvent('logout');
  // The next account on this device must not inherit this account's Meta consent.
  await setMetaAccount(null);
  clearPrivacyConsents();
  setBackendUserProvisioned(false);
  disconnectSocket();
  return Promise.all([FirebaseAuthentication.signOut(), signOut(firebaseAuth)]);
}

export async function signup(payload: SignupPayload) {
  const { email, password, firstName, lastName, dateOfBirth, guardianPermission } = payload;
  const firebaseUser = await createUserWithEmailAndPassword(firebaseAuth, email, password);
  let registeredUser: User;
  try {
    // Register before anything else can reach an authenticated endpoint, and
    // before the slow verification-email round trip. Every other endpoint 401s
    // until this document exists, which would force a logout mid-signup.
    const res = await httpService.post<RegisterResponse>('auth/register', {
      firstName,
      lastName,
      dateOfBirth,
      guardianPermission,
    });
    registeredUser = res.data.data;
    setBackendUserProvisioned(true);
    void trackMetaRegistrationCompleted(res.data.metaEventId, registeredUser);
  } catch (err) {
    logger.error({
      message: 'Error signing up',
      data: { uid: firebaseUser.user.uid },
      error: err as Error,
    });
    setBackendUserProvisioned(false);
    await deleteUser(firebaseUser.user);
    throw err;
  }

  // The account is complete from here on. A failed verification email or
  // display-name update must not delete the Firebase user: that would strand
  // the GYMing document rather than clean anything up.
  try {
    await sendEmailVerification(firebaseUser.user);
    await updateProfile(firebaseUser.user, {
      displayName: buildDisplayName(firstName, lastName),
    });
  } catch (err) {
    logger.error({
      message: 'Signup registered but post-registration steps failed',
      data: { uid: firebaseUser.user.uid },
      error: err as Error,
    });
  }

  if (registeredUser) {
    await logEvent('sign_up');
  }
  return registeredUser;
}

export async function login({ email, password }: LoginPayload, isReauthenticating?: boolean) {
  if (isReauthenticating) {
    const credential = EmailAuthProvider.credential(email, password);
    const res = await reauthenticateWithCredential(firebaseAuth.currentUser!, credential);
    return res.user;
  }
  await signInWithEmailAndPassword(firebaseAuth, email, password);
}

/**
 * Sends Firebase's password-reset email. The user sets the new password on
 * Firebase's hosted page, which only changes the credential of the existing
 * Firebase user, so the UID (and every GYMing record keyed by it) is kept.
 *
 * Resolves the same way whether or not an account exists for the address.
 */
export async function requestPasswordReset(rawEmail: string) {
  const email = rawEmail.trim();
  try {
    await sendPasswordResetEmail(firebaseAuth, email, {
      url: buildPasswordResetContinueUrl(environment.appWebBaseUrl),
      handleCodeInApp: false,
    });
  } catch (error) {
    if (isAccountNotFoundError(error)) return;
    if (!isContinueUrlError(error)) throw error;

    // A rejected continue URL is a console configuration problem, not a reason
    // to leave the user locked out: send the email without the return button.
    logger.error({
      message: 'Password reset continue URL rejected; sending reset email without it',
      data: { code: (error as { code?: string }).code },
      error: error as Error,
    });
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
    } catch (fallbackError) {
      if (isAccountNotFoundError(fallbackError)) return;
      throw fallbackError;
    }
  }
}

export const loginWithGoogle = async () => {
  try {
    const result = await FirebaseAuthentication.signInWithGoogle({ scopes: ['email', 'profile'] });

    if (!result.credential?.idToken) {
      // User likely cancelled - return null to indicate cancellation
      // Don't throw or log - this is expected user behavior
      return null;
    }

    const credential = GoogleAuthProvider.credential(result.credential.idToken);
    const firebaseRes = await signInWithCredential(firebaseAuth, credential);

    return afterSocialSignIn(firebaseRes, result.additionalUserInfo?.isNewUser === true);
  } catch (error) {
    // Check if this is a user cancellation error - if so, return null silently
    if (isUserCancellationError(error)) {
      return null;
    }

    // Real error - rethrow
    throw error;
  }
};

export const loginWithApple = async () => {
  try {
    // @ts-expect-error no need for clientId
    const { response } = await SignInWithApple.authorize({
      scopes: 'email name',
    });

    if (!response?.identityToken) {
      // User likely cancelled - return null to indicate cancellation
      return null;
    }

    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const credential = provider.credential({
      idToken: response.identityToken,
    });
    const firebaseRes = await signInWithCredential(firebaseAuth, credential);
    return afterSocialSignIn(firebaseRes);
  } catch (error) {
    // Check if this is a user cancellation error - if so, return null silently
    if (isUserCancellationError(error)) {
      return null;
    }

    // Real error - rethrow
    throw error;
  }
};

export async function changePassword(payload: ChangePasswordPayload) {
  try {
    const user = firebaseAuth.currentUser;

    if (!user) {
      throw new Error('No authenticated user found');
    }
    const credential = EmailAuthProvider.credential(user.email!, payload.prevPassword);
    await reauthenticateWithCredential(user, credential);

    await updatePassword(user, payload.newPassword);
  } catch (err) {
    logger.error({
      message: 'Error changing password',
      data: {},
      error: err as Error,
    });
    throw err;
  }
}
export const deleteAuthAccount = async () => {
  await refreshToken();
  // await Promise.all([FirebaseAuthentication.deleteUser(), deleteUser(firebaseAuth.currentUser!)]);
};
const refreshToken = async () => {
  await firebaseAuth.currentUser?.getIdToken();
};
/**
 * Google / Apple: an identity Firebase created in this sign-in has no GYMing account yet. It is
 * registered only after the date-of-birth step (views/complete-registration), never here.
 * With native auth (skipNativeAuth: false) Google signs in natively first, which creates the
 * Firebase user, so the web SDK's result says "not new": the native result decides then.
 */
const afterSocialSignIn = (firebaseRes: UserCredential, nativeIsNewUser = false): { needsRegistration: boolean } => {
  if (!nativeIsNewUser && getAdditionalUserInfo(firebaseRes)?.isNewUser !== true) return { needsRegistration: false };
  setPendingRegistration({ uid: firebaseRes.user.uid, createdInThisAttempt: true });
  return { needsRegistration: true };
};

/**
 * The date-of-birth step: creates the GYMing account for the signed-in Firebase identity
 * (a new Google / Apple identity, or one whose earlier registration never completed).
 */
export async function completeRegistration(input: { dateOfBirth: string; guardianPermission: boolean }) {
  const current = firebaseAuth.currentUser;
  if (!current) throw new Error('Not signed in');
  const [first, last] = (current.displayName ?? '').split(' ');
  const res = await httpService.post<RegisterResponse>('auth/register', {
    firstName: sanitizeNamePart(first),
    lastName: sanitizeNamePart(last),
    avatarUrl: current.photoURL || null,
    dateOfBirth: input.dateOfBirth,
    guardianPermission: input.guardianPermission,
  });
  const user = res.data.data;
  setBackendUserProvisioned(true);
  clearPendingRegistration();
  void trackMetaRegistrationCompleted(res.data.metaEventId, user);
  await logEvent('sign_up');
  return user;
}

/**
 * Cancelled date-of-birth step (or under 13): signs out. The Firebase identity is deleted only
 * when this app session created it in the current sign-in, so an existing identity is never
 * deleted (for example one whose earlier registration never completed).
 */
export async function abandonRegistration() {
  const pending = getPendingRegistration();
  const current = firebaseAuth.currentUser;
  clearPendingRegistration();
  if (pending?.createdInThisAttempt && current && current.uid === pending.uid) {
    try {
      await deleteUser(current);
    } catch (error) {
      logger.info('[Auth] could not delete an unregistered new identity', {
        code: (error as { code?: string }).code ?? null,
      });
    }
  }
  await logout();
}

/**
 * Newest `appVersions` doc carrying each flag.
 *
 * Deliberately unbounded: a `forceUpdate` doc can be arbitrarily far back in history,
 * so a blanket `limit()` on this date-ordered query could silently skip a mandatory
 * update. Narrowing it safely needs flag-filtered queries with composite indexes.
 */
const getCurrentAndLatestAppVersion = async (): Promise<AppVersionCandidates | null> => {
  const q = query(
    collection(firestore, 'appVersions'),
    where('platform', '==', Capacitor.getPlatform()),
    orderBy('date', 'desc')
  );
  const docs = await getDocs(q);
  if (docs.empty) {
    return null;
  }
  const items = docs.docs.map(doc => doc.data() as AppVersion);
  return {
    latestVersion: items.find(item => item.liveUpdate),
    forceUpdateVersion: items.find(item => item.forceUpdate),
  };
};

/**
 * Both startup update decisions from a single Firestore read.
 *
 * These used to be two exported helpers that each ran the identical `appVersions`
 * query, costing two sequential round trips on every native cold start.
 */
export const getAppVersionStatus = async (): Promise<AppVersionStatus> => {
  try {
    if (Capacitor.getPlatform() === 'web') return NO_APP_VERSION_UPDATES;
    const res = await getCurrentAndLatestAppVersion();
    if (!res) return NO_APP_VERSION_UPDATES;
    return resolveAppVersionStatus(environment.version, res);
  } catch (err) {
    logger.error({
      message: 'Error checking app version status',
      error: err as Error,
    });
    return NO_APP_VERSION_UPDATES;
  }
};

export const downloadAndApplyLiveUpdate = async (appVersion: AppVersion) => {
  try {
    const currentBundle = await LiveUpdate.getBundles();
    if (currentBundle.bundleIds.includes(appVersion.version)) {
      await LiveUpdate.setNextBundle({ bundleId: appVersion.version });
      await LiveUpdate.reload();
      return;
    }
  } catch (err) {
    logger.error({
      message: 'Error getting current bundle',
      error: err as Error,
    });
  }
  try {
    await LiveUpdate.downloadBundle({
      url: `${environment.liveUpdateBaseUrl}/dist-${appVersion.version}.zip`,
      bundleId: appVersion.version,
    });
    await LiveUpdate.setNextBundle({ bundleId: appVersion.version });
    await LiveUpdate.reload();
  } catch (err) {
    logger.error({
      message: 'Error downloading and applying live update',
      data: { appVersion },
      error: err as Error,
    });
    const message = err instanceof Error ? err.message : 'Unknown error';
    if (message.includes('exists')) {
      await LiveUpdate.setNextBundle({ bundleId: appVersion.version });
      await LiveUpdate.reload();
    }
  }
};
